import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../../lib/api'
import { Spinner } from '../Spinner'
import type { MambuConnectionCatalogItem, MambuConnectionCatalogResponse } from '../../lib/types'
import type { ProductView } from './ProductViewToggle'

/* Shared components and hooks used by both MambuMcp and McpAnalytics. */

export function useMcpCatalogState() {
  const apiFetch = useApiFetch()
  const [view, setView] = useState<ProductView>('list')
  const [toast, setToast] = useState<string | null>(null)
  /* Keyed by item.key so the same vote state is shared between grid and list
     renderings of the same product — see PreviewInterestVote prop docs. */
  const [voted, setVoted] = useState<Record<string, 'up' | 'down'>>({})
  const i18n = usePreviewI18n()

  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 2500)
    return () => clearTimeout(id)
  }, [toast])

  const catalogQuery = useQuery({
    queryKey: ['mambu-connections', 'catalog'],
    queryFn: () => apiFetch<MambuConnectionCatalogResponse>('/api/v1/mambu-connections'),
  })

  return {
    view, setView,
    toast, setToast,
    voted, setVoted,
    catalogQuery,
    items: catalogQuery.data?.items ?? [],
    isLoading: catalogQuery.isLoading,
    error: catalogQuery.error,
    ...i18n,
  }
}

export function usePreviewI18n() {
  const { t } = useTranslation()

  function previewDescription(item: MambuConnectionCatalogItem) {
    return item.key === 'payments'
      ? t('mambuMcp.preview.payments.description')
      : t('mambuMcp.preview.insights.description')
  }

  function previewRequires(item: MambuConnectionCatalogItem) {
    return item.key === 'payments'
      ? t('mambuMcp.preview.payments.requiresSubscription')
      : t('mambuMcp.preview.insights.requiresSubscription')
  }

  function previewDisabledTitle(item: MambuConnectionCatalogItem) {
    return item.key === 'payments'
      ? t('mambuMcp.preview.payments.disabledActionTitle')
      : t('mambuMcp.preview.insights.disabledActionTitle')
  }

  return { previewDescription, previewRequires, previewDisabledTitle }
}

export function McpNameWithBadge({ item }: { readonly item: MambuConnectionCatalogItem }) {
  const { t } = useTranslation()
  return (
    <div className="agent-grid-name">
      {item.name}
      {item.status === 'preview' && (
        <>{' '}<span className="nav-badge-soon">{t('common.preview')}</span></>
      )}
    </div>
  )
}

interface McpPreviewGridCardProps {
  readonly item: MambuConnectionCatalogItem
  readonly avatar: ReactNode
  readonly reqBadge: ReactNode
  readonly description: string
  readonly voteButtons: ReactNode
  readonly actionButton: ReactNode
}

export function McpPreviewGridCard({ item, avatar, reqBadge, description, voteButtons, actionButton }: McpPreviewGridCardProps) {
  return (
    <div className="agent-grid-card">
      <div className="agent-grid-card-header" style={{ alignItems: 'center' }}>
        {avatar}
        {reqBadge}
      </div>
      <McpNameWithBadge item={item} />
      <div className="agent-grid-desc">{description}</div>
      <div className="agent-grid-footer">
        <div />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="vote-btns" style={{ marginTop: 0, gap: 8 }}>
            {voteButtons}
          </div>
          {actionButton}
        </div>
      </div>
    </div>
  )
}

interface McpPreviewListRowProps {
  readonly item: MambuConnectionCatalogItem
  readonly avatar: ReactNode
  readonly description: string
  readonly reqBadge: ReactNode
  readonly voteButtons: ReactNode
  readonly actionButton: ReactNode
}

export function McpPreviewListRow({ item, avatar, description, reqBadge, voteButtons, actionButton }: McpPreviewListRowProps) {
  return (
    <div className="agent-list-row">
      {avatar}
      <div className="agent-list-info">
        <McpNameWithBadge item={item} />
        <div className="agent-grid-desc">{description}</div>
      </div>
      <div className="agent-list-actions" style={{ gap: 10 }}>
        {reqBadge}
        {voteButtons}
        {actionButton}
      </div>
    </div>
  )
}

export function PreviewDisabledButton({ title, icon }: { readonly title: string; readonly icon: ReactNode }) {
  return (
    <button
      type="button"
      className="skill-edit-btn"
      title={title}
      aria-label={title}
      disabled
      style={{ opacity: 0.35, cursor: 'not-allowed', color: 'var(--text-tertiary)' }}
    >
      {icon}
    </button>
  )
}

export function McpCatalogBody({ isLoading, error, errorMessage, children }: {
  readonly isLoading: boolean
  readonly error: unknown
  readonly errorMessage: string
  readonly children: ReactNode
}) {
  return (
    <div className="page-body mcp-catalog-body">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spinner size={32} />
        </div>
      )}
      {!!error && !isLoading && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 8, padding: '16px 20px', color: 'var(--danger)', fontSize: 13 }}>
          {errorMessage}
        </div>
      )}
      {!isLoading && !error && <>{children}</>}
    </div>
  )
}
